import { ChangeEventHandler, KeyboardEvent, ReactNode, useCallback, useLayoutEffect, useRef, useState } from 'react';

import { Loader } from 'components/organization';
import { useDebounce } from 'hooks';

import styles from '../searchable-select/searchable-select.module.scss';
import './async-search.scss';

export type AsyncSearchProps<T extends { id: number } = { id: number; [key: string]: any }> = {
  id?: string;
  label?: string;
  value?: T | null;
  required?: boolean;
  formatOption?: (val: T) => string;
  formatValue: (val?: T | null) => string;
  handleSelect: (val?: T, inputElement?: HTMLInputElement | null) => any;
  search: (input: string) => Promise<T[]>;
  selected?: Set<number>;
  adornment?: ReactNode;
  disabled?: boolean;
  /** Controlled domain validation, composed with the required dropdown selection. */
  validationMessage?: string;
  ['data-cy']?: string;
};

const AsyncSearch = <T extends { id: number }>({
  id,
  label,
  value,
  required,
  formatOption,
  formatValue,
  handleSelect,
  search,
  selected,
  adornment,
  disabled,
  validationMessage,
  ...props
}: AsyncSearchProps<T>) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const [options, setOptions] = useState<T[] | undefined>(value ? [value] : []);

  useLayoutEffect(() => {
    const selectionMessage = !value?.id && required ? 'Please select from the dropdown' : '';
    inputRef.current?.setCustomValidity(selectionMessage || validationMessage || '');
  }, [value, required, validationMessage]);

  /* prettier-ignore */
  const handleSearch: ChangeEventHandler<HTMLInputElement> = useCallback(async e => {
    setOptions(undefined);
    setOptions(await search(e.target.value));
  }, [search, setOptions]);

  const { debounce: debounceSearch } = useDebounce(handleSearch);

  /* prettier-ignore */
  const handleClick = useCallback(async (option?: T) => {
    const response = await handleSelect(option, inputRef.current);
    if (inputRef.current) {
      inputRef.current.value = formatValue(option);
      if (typeof response === 'string') {
        inputRef.current?.setCustomValidity(response);
        inputRef.current?.reportValidity();
      } else if (!option) {
        setTimeout(() => inputRef.current?.focus())
      } else {
        inputRef.current?.blur();
      }
    }
  }, [handleSelect, formatValue]);

  const handleKeyDown = useCallback(
    (option?: T) => (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.code === 'ArrowDown' || e.code === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();
        let item: Element | null | undefined;
        if (e.target instanceof HTMLInputElement) {
          item = menuRef.current?.firstElementChild;
        } else if (e.target instanceof HTMLElement) {
          item = e.code === 'ArrowUp' ? e.target.previousElementSibling : e.target.nextElementSibling;
        }
        if (item instanceof HTMLElement) item.focus();
      } else if (e.code === 'Enter') handleClick(option);
    },
    [handleClick]
  );

  return (
    <div className={'async-search ' + styles['searchable-select']}>
      <input
        type="text"
        ref={inputRef}
        required={required}
        disabled={disabled}
        defaultValue={formatValue(value)}
        onKeyDown={handleKeyDown()}
        onChange={debounceSearch}
        className={!value ? 'blank' : ''}
        data-cy={props['data-cy']}
      />
      {!!label && (
        <label htmlFor={id} className={inputRef.current?.value ? 'active' : ''}>
          {label}
          {required && ' *'}
        </label>
      )}
      <svg
        role="button"
        className={styles['clear-btn']}
        focusable="false"
        aria-hidden="true"
        viewBox="0 0 24 24"
        onClick={() => !disabled && handleClick()}
      >
        <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"></path>
      </svg>
      {adornment ?? <span className="material-icons icon-large search">search</span>}
      <div className="dropdown">
        <Loader loading={options === undefined}>
          <div className={styles.dropdown} ref={menuRef}>
            {options?.map((option, i) => (
              <div
                role="option"
                key={option.id}
                tabIndex={i}
                aria-selected={option.id === value?.id || selected?.has(option.id)}
                className={`pointer ${styles['dropdown-option']} ${!selected?.has(option.id) ? '' : 'selected'}`}
                onClick={e => {
                  handleClick(option);
                  e.currentTarget.blur();
                }}
                onKeyDown={handleKeyDown(option)}
                data-cy={`${props['data-cy'] ?? 'async-search'}-option-${option.id}`}
              >
                {formatOption?.(option) ?? formatValue(option)}
              </div>
            ))}
          </div>
        </Loader>
      </div>
    </div>
  );
};

export default AsyncSearch;
